import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UnitValueComponent } from './unit-value/unit-value.component';
import { PropertyPipe } from './property.pipe';
import { ProcessingPopupComponent } from '../results/scenario-comparison/scenario-list/scenario-modify-dialog/processing-popup/processing-popup.component';
import { CustomSnackbarComponent } from './custom-snackbar/custom-snackbar.component';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { NouisliderModule } from 'ng2-nouislider';
import { PlotlyViaWindowModule } from 'angular-plotly.js';
import { CUSTOM_DATE_FORMATS, CustomDateAdapter } from './date-adapter/date-adapter';
import { TableComponent } from './table/table.component';
import { FlareDialogComponent } from './flare/flare-dialog.component';
import { ProjectGuard } from './project-guard';
import { CanDeactivateDialogComponent } from './can-deactivate/can-deactivate-dialog/can-deactivate-dialog.component';
import { UnitValueValidatorDirective } from './unit-value/unit-value-validator/unit-value.validator';
import { InputSummaryComponent } from './input-summary/input-summary.component';
import { ToastrModule } from 'ngx-toastr';
import { InputSummarySubtableComponent } from './input-summary/input-summary-subtable/input-summary-subtable.component';
import { PercentSliderComponent } from './percent-slider/percent-slider.component';
import { SaveCancelButtonsComponent } from './save-cancel-buttons/save-cancel-buttons.component';
import { ConfirmationPopUpComponent } from './save-cancel-buttons/confirmation-pop-up/confirmation-pop-up.component';
import { SensitivityTableItemValidatorDirective } from '../results/sensitivity/sensitivity-helper/sensitivity-table-item-validator.directive';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MaintenanceModeComponent } from './maintenance-mode/maintenance-mode.component';
import { ConfirmationDialogComponent } from './confirmation-dialog-component/confirmation-dialog.component';
import { FlowDiagramCreatorComponent } from './flow-diagram-creator/flow-diagram-creator.component';
import { MatSliderModule } from '@angular/material/slider';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AnessaTranslationDirective } from './translation/anessa-translation.directive';
import { AnessaTranslationPipe } from './translation/anessa-translation.pipe';
import { GetNameDialogComponent } from './get-name-dialog/get-name-dialog.component';
import { InputAppComponent } from './input-app/input-app.component';
import { InputAppValidatorDirective } from './input-app/input-app.validator';
import { PlotlyComponent } from './plotly/plotly.component';
import { ResultTableComponent } from './result-table/result-table.component';
import { ResultSetConfigBtnComponent } from './result-set-config-btn/result-set-config-btn.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ValidatedInputComponent } from './validated-input/validated-input.component';
import { ValidatedInputValidatorDirective } from './validated-input/validated-input-validator/validated-input.validator';
import { PlatformPickerDialogComponent } from '../header/topbar/platform-picker-dialog/platform-picker-dialog.component';
import { MatIcon } from '@angular/material/icon';
import { SimulatorScenarioConfirmationPopup } from './scenario-comparison/simulator-scenario-confirmation-popup/simulator-scenario-confirmation-popup.component';
import { NoWhitespaceDirective } from './no-whitespace-directive/no-whitespace.directive';
import { TrimWhitespaceDirective } from './no-whitespace-directive/trim-whitespace.directive';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [
    UnitValueComponent,
    UnitValueValidatorDirective,
    PropertyPipe,
    ProcessingPopupComponent,
    CustomSnackbarComponent,
    TableComponent,
    FlareDialogComponent,
    CanDeactivateDialogComponent,
    InputSummaryComponent,
    InputSummarySubtableComponent,
    PercentSliderComponent,
    SaveCancelButtonsComponent,
    ConfirmationPopUpComponent,
    SensitivityTableItemValidatorDirective,
    MaintenanceModeComponent,
    ConfirmationDialogComponent,
    FlowDiagramCreatorComponent,
    AnessaTranslationDirective,
    AnessaTranslationPipe,
    PlotlyComponent,
    GetNameDialogComponent,
    InputAppComponent,
    InputAppValidatorDirective,
    ResultTableComponent,
    ValidatedInputComponent,
    ValidatedInputValidatorDirective,
    ResultSetConfigBtnComponent,
    PlatformPickerDialogComponent,
    SimulatorScenarioConfirmationPopup,
    NoWhitespaceDirective,
    TrimWhitespaceDirective
  ],
  exports: [
    CommonModule,
    UnitValueComponent,
    PropertyPipe,
    TableComponent,
    InputSummaryComponent,
    PercentSliderComponent,
    SaveCancelButtonsComponent,
    ConfirmationPopUpComponent,
    SensitivityTableItemValidatorDirective,
    FlowDiagramCreatorComponent,
    AnessaTranslationDirective,
    AnessaTranslationPipe,
    PlotlyComponent,
    GetNameDialogComponent,
    InputAppComponent,
    ResultTableComponent,
    ResultSetConfigBtnComponent,
    ValidatedInputComponent,
    NoWhitespaceDirective,
    TrimWhitespaceDirective,
  ], imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatTableModule,
    ToastrModule.forRoot({
      newestOnTop: true,
      positionClass: 'toast-bottom-right',
      maxOpened: 5,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      defaultLanguage: 'en'
    }),
    MatButtonModule,
    NouisliderModule,
    PlotlyViaWindowModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatIcon], providers: [
    {
      provide: DateAdapter, useClass: CustomDateAdapter
    },
    {
      provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS
    },
    ProjectGuard,
    provideHttpClient(withInterceptorsFromDi()),
  ]
})

export class SharedModule {
}
